import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apuracaoService } from "../services/apuracaoService";
import type {
  ApuracaoIcms,
  ApuracaoIrpjCsll,
  ApuracaoPisCofins,
  TipoApuracao,
} from "../types/apuracao";

const KEYS = {
  icms: (empresaId: number, ano: number) => ["apuracoes", "icms", empresaId, ano] as const,
  pisCofins: (empresaId: number, ano: number) => ["apuracoes", "pis-cofins", empresaId, ano] as const,
  irpjCsll: (empresaId: number, ano: number) => ["apuracoes", "irpj-csll", empresaId, ano] as const,
  detalhe: (tipo: TipoApuracao, id: number) => ["apuracoes", "detalhe", tipo, id] as const,
};

export function useApuracoesIcms(empresaId: number, ano: number) {
  return useQuery({
    queryKey: KEYS.icms(empresaId, ano),
    queryFn: () => apuracaoService.listarIcms(empresaId, ano),
    enabled: empresaId > 0 && ano > 0,
  });
}

export function useApuracoesPisCofins(empresaId: number, ano: number) {
  return useQuery({
    queryKey: KEYS.pisCofins(empresaId, ano),
    queryFn: () => apuracaoService.listarPisCofins(empresaId, ano),
    enabled: empresaId > 0 && ano > 0,
  });
}

export function useApuracoesIrpjCsll(empresaId: number, ano: number) {
  return useQuery({
    queryKey: KEYS.irpjCsll(empresaId, ano),
    queryFn: () => apuracaoService.listarIrpjCsll(empresaId, ano),
    enabled: empresaId > 0 && ano > 0,
  });
}

export function useApuracaoDetalhe(tipo: TipoApuracao, id: number) {
  return useQuery({
    queryKey: KEYS.detalhe(tipo, id),
    queryFn: () => apuracaoService.detalhe(tipo, id),
    enabled: id > 0,
  });
}

export function useCalcularApuracao(tipo: TipoApuracao) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      empresaId,
      periodo,
    }: {
      empresaId: number;
      periodo: string;
    }): Promise<ApuracaoIcms | ApuracaoPisCofins | ApuracaoIrpjCsll> => {
      if (tipo === "icms") return apuracaoService.calcularIcms(empresaId, periodo);
      if (tipo === "pis-cofins") return apuracaoService.calcularPisCofins(empresaId, periodo);
      return apuracaoService.calcularIrpjCsll(empresaId, periodo);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apuracoes"] });
      toast.success("Apuração calculada com sucesso.");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem
        ?? "Falha ao calcular apuração.";
      toast.error(msg);
    },
  });
}

export function useFecharApuracao(tipo: TipoApuracao) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, usuarioId }: { id: number; usuarioId: number }) =>
      apuracaoService.fechar(tipo, id, usuarioId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apuracoes"] });
      toast.success("Apuração fechada.");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem
        ?? "Falha ao fechar apuração.";
      toast.error(msg);
    },
  });
}

export function useReabrirApuracao(tipo: TipoApuracao) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, usuarioId }: { id: number; usuarioId: number }) =>
      apuracaoService.reabrir(tipo, id, usuarioId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apuracoes"] });
      toast.success("Apuração reaberta.");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem
        ?? "Falha ao reabrir apuração.";
      toast.error(msg);
    },
  });
}
